import { Mail } from 'lucide-react';
import { Fragment } from 'react';

// Vendor email fields can contain a whole address book, including quoted
// names with commas and adjacent angle-bracket addresses. This is display
// parsing only: keep unrecognized text visible and never change stored data.
function splitContacts(value: string): Array<string> {
    const contacts: Array<string> = [];
    let start = 0;
    let quoted = false;
    let bracketed = false;
    for (let index = 0; index < value.length; index++) {
        const character = value[index];
        if (character === '"' && value[index - 1] !== '\\') {
            quoted = !quoted;
        }
        if (character === '<') {
            bracketed = true;
        }
        if (character === '>') {
            contacts.push(value.slice(start, index + 1));
            start = index + 1;
            bracketed = false;
            quoted = false;
        } else if (
            (!quoted || /^["“”\s]*$/.test(value.slice(start, index))) &&
            !bracketed &&
            (character === ',' || character === ';' || character === '\n')
        ) {
            contacts.push(value.slice(start, index));
            start = index + 1;
            quoted = false;
        }
    }
    contacts.push(value.slice(start));
    return contacts
        .map((contact) => contact.trim())
        .filter((contact) => contact && !/^["“”]+$/.test(contact));
}

export function ImportedContactEmails({ value }: { readonly value: string }) {
    return (
        <ul aria-label="Email contacts" className="space-y-2 py-1">
            {splitContacts(value).map((contact, index) => {
                const named = /^(.*?)<\s*([^<>]+)\s*>$/.exec(contact);
                const name = named?.[1]
                    ?.trim()
                    .replace(/^["“”]+|["“”]+$/g, '')
                    .trim();
                const email = (named?.[2] ?? contact)
                    .trim()
                    .replace(/^["“”]+|["“”]+$/g, '');
                const valid =
                    /^[^\s<>"(),;:]+@[^\s<>"(),;:]+\.[^\s<>"(),;:]+$/.test(
                        email,
                    );
                return (
                    <li
                        key={index}
                        className="flex min-w-0 items-start gap-2 text-sm"
                    >
                        <Mail className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <div className="min-w-0 space-y-0.5 [overflow-wrap:anywhere]">
                            {valid ? (
                                <Fragment>
                                    {name && (
                                        <p className="font-medium">{name}</p>
                                    )}
                                    <a
                                        href={`mailto:${encodeURIComponent(email)}`}
                                        className="text-blue-600 underline decoration-blue-600/40 underline-offset-2 dark:text-blue-400"
                                    >
                                        {email}
                                    </a>
                                </Fragment>
                            ) : (
                                <p>{contact}</p>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
